import { ClusterOutlined, CopyOutlined, DeleteOutlined, DownloadOutlined, InfoCircleOutlined, TagsOutlined } from '@ant-design/icons';
import { Popover, Select, Space } from 'antd';
import { Components } from '@fasstech/flexiget';
import { any, compose, equals, filter, find, isNil, map, path, pathEq, prop, propEq, assoc, propOr, or, isNotNil } from 'ramda';
import { notEqual, propNotEq } from 'ramda-adjunct';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTreeContext } from '../../../../components/treeStructure/treeContext';
import useDocumentsTable from '../../../../hooks/useDocumentsTable';
import SelectTags from '../../../docs/components/document/SelectTags';
import { updateKeys } from '../../../../components/treeStructure/treeHelper';
import { errorMessage } from '../../../../utils/messageMutation';
import DuplicateDocumentAndItsWidgets from '../../../../_graphql/mutations/documents/DuplicateDocumentAndItsWidgets';
import { setElementClass } from '../../../helpers/setElementClass';
import GenerateStructuredHtmlMutation from '../../../../_graphql/mutations/documents/GenerateStructuredHtmlMutation';
import { downloadHtmlFile } from '../../../../utils/downloadHtmlFile';
import OperationStatusSection from '../../../../components/OperationStatusSection';
import withUser from '../../../../withUser';
import { FinishedDuplicateDocumentSubscriptionQuery } from '../../../../_graphql/subscriptions/duplicateDocument/FinishedDuplicateDocumentSubscription';
import ProgressModalSubscription from '../../../../components/ProgressModalSubscription';
import AnalyticsTagsForm from '../../../docs/components/document/documentFormTabs/createTab/AnalyticsTagsForm';
import ConfirmButton from '../../../../components/ConfirmButton';
import BaseModal from '../../../../components/BaseModal';
import useActionGuard from '../../../../hooks/useActionGuard';
import { sortByNameCaseInsensitive } from '../../../helpers/sortByNameCaseInsensitive';
import { QOffersQuery } from '../../../../_graphql/queries/QOffers';
import useFetchQuery from '../../../../hooks/useFetchQuery';
import useOfferRole from '../../../../hooks/useOfferRole';
import { canEditOfferItem, hasRole } from '../../../../components/users/utils/roles';
import { getCurrentUserId, isUserAdmin } from '../../../helpers/isUserAdmin';
import { ROLES } from '../../../../lib/rights';

const DocumentRights = ({
  doc,
  offerId,
  scope,
  setDocuments,
  folder,
  refetch,
  setRefetchWidgets,
  isPublished,
  isReview,
  documentName,
  teams,
  userTeam,
  setFullscreenLoading,
  setRefetchTags,
  reFetchFolders,
  user
}) => {
  const { userIsAdmin, userIsOfferEditor } = useOfferRole(offerId);
  const isDraft = !isPublished && !isReview;
  const canEdit = canEditOfferItem({
    userIsAdmin, userIsOfferEditor, scope, isDraft 
  });
  const { deleteDocument } = useDocumentsTable();
  const { loading: actionLoading, run } = useActionGuard();
  const { removeTree, expandedKeys, setExpandedKeys, trees } = useTreeContext();
  const [isOpenModalDuplicateDocument, setIsOpenModalDuplicateDocument] = useState(false);
  const hasFetchedOffersRef = useRef(false);
  const currentOfferIsNotDraft = or(isPublished, isReview);
  const userTeamId = prop('teamId', userTeam);
  const [duplicateOn, setDuplicateOn] = useState({
    teamId: userTeamId,
    offerId: currentOfferIsNotDraft ? '' : offerId
  });
  const [isOpen, setIsOpen] = useState(false);
  const [errorDuplicateDocument, setErrorDuplicateDocument] = useState(false);

  const documentId = prop('elmId', doc);
  const treeId = prop('treeId', doc);
  const userId = getCurrentUserId(user);
  const selectedTeamId = prop('teamId', duplicateOn);
  const targetOfferId = prop('offerId', duplicateOn);
  const offersQueryArgs = useMemo(() => ({
    filter: {
      status: 'draft',
      hideArchived: true
    }
  }), []);

  const { offers, isLoading: isLoadingOffers, reFetch: reFetchOffers } = useFetchQuery({
    // Keep query idle until the first modal open, then keep data in memory across reopenings.
    skip: true,
    query: QOffersQuery,
    args: offersQueryArgs,
    dataProp: 'offers',
    defaultData: []
  });

  const handleOpenDuplicateModal = useCallback(() => {
    if (!hasFetchedOffersRef.current) {
      hasFetchedOffersRef.current = true;
      reFetchOffers();
    }
    setIsOpenModalDuplicateDocument(true);
  }, [reFetchOffers]);
  
  const sortedTeams = useMemo(() => sortByNameCaseInsensitive('name')(teams), [teams]);
  const userTeamRoles = propOr([], 'roles', userTeam);
  const getUserRolesForTeam = useCallback((team) => {
    if (equals(prop('id', team), userTeamId)) return userTeamRoles;
    const member = find(pathEq(userId, ['user', 'id']), propOr([], 'members', team));
    return propOr([], 'roles', member);
  }, [userTeamId, userTeamRoles, userId]);

  const availableTeams = useMemo(() => filter((team) => {
    if (isUserAdmin(user, team)) return true;
    const roles = getUserRolesForTeam(team);
    return any(propEq(ROLES.offerEditor, 'role'), roles);
  }, sortedTeams), [sortedTeams, user, getUserRolesForTeam]);

  const availableTeamsWithOffers = useMemo(() => filter((team) => {
    const teamId = prop('id', team);
    const isAdmin = isUserAdmin(user, team);
    const roles = getUserRolesForTeam(team);

    return any((offer) => {
      const isOfferInTeam = equals(teamId, prop('team', offer));
      if (!isOfferInTeam) return false;
      return isAdmin || hasRole(ROLES.offerEditor, roles, prop('id', offer));
    }, offers);
  }, availableTeams), [availableTeams, getUserRolesForTeam, offers, user]);

  const selectedTeam = useMemo(() => find(propEq(selectedTeamId, 'id'), availableTeamsWithOffers), [selectedTeamId, availableTeamsWithOffers]);

  const userRolesForSelectedTeam = useMemo(() => {
    if (isNil(selectedTeam)) return [];
    return getUserRolesForTeam(selectedTeam);
  }, [selectedTeam, getUserRolesForTeam]);

  const filteredOffers = useMemo(() => {
    if (isNil(selectedTeam)) return [];
    const isAdmin = isUserAdmin(user, selectedTeam);

    return filter(
      (offer) => equals(selectedTeamId, prop('team', offer))
        && (isAdmin || hasRole(ROLES.offerEditor, userRolesForSelectedTeam, prop('id', offer))),
      offers
    );
  }, [offers, selectedTeamId, userRolesForSelectedTeam, selectedTeam, user]);

  const handleDeleteDocument = () => {
    const refetchTags = () => setRefetchTags && setRefetchTags(true);

    run(`delete-document-${documentId}`, (done) => {
      if (isNil(treeId)) {
        const nodeKey = compose(
          path([0, 'key']),
          filter(pathEq(offerId, ['element', 'offerId']))
        )(trees);

        const updatedKeys = updateKeys(expandedKeys, [nodeKey], 'remove');

        setExpandedKeys(updatedKeys);
        deleteDocument(documentId, (ok) => {
          if (ok) {
            setDocuments(filter(propNotEq(documentId, 'id')));
            refetchTags();
          }
          done(ok);
        })();
      } else {
        removeTree(treeId, folder);
        deleteDocument(documentId, (ok) => {
          if (ok) {
            setDocuments(filter(propNotEq(documentId, 'id')));
            refetchTags();
          }
          done(ok);
        })();
      }
    });

    setRefetchWidgets && setRefetchWidgets(true);
  };

  const handleDuplicateDocument = () => {
    setIsOpen(true);
    run(`duplicate-document-${documentId}`, (done) => DuplicateDocumentAndItsWidgets({ documentId, offerId: targetOfferId }, done), (ok, error) => setErrorDuplicateDocument(error));
  };

  const handleDownloadStructuredHtml = () => {
    setFullscreenLoading(true);

    run(`download-structured-html-${documentId}`, (done) => GenerateStructuredHtmlMutation(
      { offerId, documentId },
      (ok, error, html, filename) => {
        setFullscreenLoading(false);

        if (ok && !error) {
          downloadHtmlFile(html, filename);
        } else {
          errorMessage();
        }

        done(ok, error, html, filename);
      }
    ));
  };

  const onChangeTeamDuplicateOn = (teamId) => {
    setDuplicateOn(assoc('teamId', teamId));
    if (notEqual(teamId, userTeamId)) {
      setDuplicateOn(assoc('offerId', null));
    }
  };

  const handleModalLoadingClick = () => {
    if (equals(targetOfferId, offerId) && isNotNil(refetch)) {
      reFetchFolders();
      refetch();
      setRefetchWidgets(true);
    }
  };
  const extractStatus = response => response.finishedDuplicateDocumentSubscription;

  const initialStatusDuplicateDocument = {
    step: 0,
    document: false,
    widgets: false,
    globalVariables: false
  };

  const offersSelectIsLoading = isLoadingOffers || !hasFetchedOffersRef.current;

  return (
    <Space>
      <Components.Tooltip
        title="Export HTML"
      >
        <DownloadOutlined onClick={handleDownloadStructuredHtml} className="btn-download-html hover:text-flexi-grey" />
      </Components.Tooltip>
      {canEdit && (
        <>
          {!isPublished && (
            <Popover
              trigger="click"
              title=""
              placement="bottom"
              content={(
                <SelectTags
                  offerId={offerId}
                  documentId={documentId}
                  setRefetchTags={setRefetchTags}
                />
              )}
            >
              <Components.Tooltip
                title="Lier à un tag"
              >
                <TagsOutlined className="btn-link-tag hover:text-flexi-grey" />
              </Components.Tooltip>
            </Popover>
          )}
          {!isPublished && (
            <Popover
              trigger="click"
              title=""
              placement="bottom"
              content={(
                <AnalyticsTagsForm
                  offerId={offerId}
                  documentId={documentId}
                  setRefetchTags={setRefetchTags}
                />
              )}
            >
              <Components.Tooltip
                title="Catégoriser le document"
              >
                <ClusterOutlined className="btn-link-tag hover:text-flexi-grey" />
              </Components.Tooltip>
            </Popover>
          )}
          <BaseModal
            visible={isOpenModalDuplicateDocument}
            onHide={() => setIsOpenModalDuplicateDocument(false)}
            title="Où souhaitez-vous dupliquer votre document ?"
            disabled={!targetOfferId}
            onClick={() => {
              handleDuplicateDocument();
              setIsOpenModalDuplicateDocument(false);
            }}
            loading={propOr(false, `duplicate-document-${documentId}`, actionLoading)}
          >
            <div className="flex flex-col">
              <b>Équipe de destination</b>
              <Select
                showSearch
                optionFilterProp="children"
                className="mt-5 w-1/2"
                size="small"
                onChange={onChangeTeamDuplicateOn}
                value={!offersSelectIsLoading ? selectedTeamId : undefined}
                placeholder="Indiquez l'équipe de destination"
                loading={isLoadingOffers}
                notFoundContent={offersSelectIsLoading ? undefined : 'Aucune équipe avec offre disponible'}
              >
                {map((team) => <Select.Option key={prop('id', team)} value={prop('id', team)}>{prop('name', team)}</Select.Option>)(availableTeamsWithOffers)}
              </Select>
              <b className="mt-2">Offre de destination</b>
              <Select
                showSearch
                optionFilterProp="children"
                className="mt-5 w-1/2"
                size="small"
                onChange={(v) => setDuplicateOn(assoc('offerId', v))}
                value={!offersSelectIsLoading ? targetOfferId : undefined}
                placeholder="Indiquez l'offre de destination"
                loading={isLoadingOffers}
                notFoundContent={offersSelectIsLoading ? undefined : 'Aucune offre disponible'}
              >
                {map((offer) => <Select.Option key={prop('id', offer)} value={prop('id', offer)}>{prop('offerName', offer)} - {path(['version', 'major'], offer)}.{path(['version', 'minor'], offer)}</Select.Option>)(filteredOffers)}
              </Select>
            </div>
          </BaseModal>
          <ProgressModalSubscription
            isOpen={isOpen}
            subscription={FinishedDuplicateDocumentSubscriptionQuery}
            variables={{ filter: { userId, itemIds: [`duplicateDocument-${documentId}`] } }}
            infoLoading={(status, progress, error) => (
              <OperationStatusSection
                operationType="la duplication du document"
                items={[
                  { label: 'Document dupliqué', type: 'document' },
                  { label: 'Widgets dupliqués', type: 'widgets' },
                  { label: 'Variables globales dupliquées', type: 'globalVariables' }
                ]}
                status={status}
                progress={progress}
                errorMsg="Impossible de dupliquer correctement le document, veuillez contacter l'équipe Flexidocs"
                showError={error}
              />
            )}
            onFinish={handleModalLoadingClick}
            extractStatus={extractStatus}
            initialStatus={initialStatusDuplicateDocument}
            setIsOpen={setIsOpen}
            error={errorDuplicateDocument}
          />
          <Components.Tooltip title="Dupliquer le document">
            <CopyOutlined onClick={handleOpenDuplicateModal} className={`${setElementClass(documentName, 'duplicate-document-button')} btn-duplicate-doc hover:text-flexi-grey`} />
          </Components.Tooltip>
          {!isPublished && (
            <Components.Tooltip title="Supprimer le document">
              <ConfirmButton
                placement="left"
                title="Êtes-vous sûr de vouloir supprimer ce document ?"
                onConfirm={handleDeleteDocument}
                buttonType="text"
                sizeButton="small"
                className="!w-3.5 !h-3.5"
                icon={<DeleteOutlined className={`hover:text-flexi-red ${setElementClass(documentName, 'delete-document-button')}`} />}
                loading={propOr(false, `delete-document-${documentId}`, actionLoading)}
              />
            </Components.Tooltip>
          )}
          {prop('sourceName', doc) && (
            <Components.Tooltip
              title={prop('sourceName', doc)}
            >
              <InfoCircleOutlined className="btn-link-tag hover:text-flexi-grey" />
            </Components.Tooltip>
          )}
        </>
      )}
    </Space>
  );
};

export default memo(withUser(DocumentRights));
